// BrandPanel (board 25 v2 §14.6): the hero-gradient column on the start side,
// 560 px (400 at 1024). Before the PIN it carries a headline, one value line
// per screen and floating category tiles — three of them photo slots (the
// merchant's photos later; the board ships Pexels examples, which are NOT
// bundled: the slots render as tinted placeholders until a platform content
// key carries real photos). After the PIN it becomes the live preview of the
// public page that fills answer by answer and ends «منشورة».

import { Car, Goal, Image as ImageIcon, Link as LinkIcon, Scissors, Stethoscope, Utensils } from 'lucide-react';
import { BrandMark, type BrandMarkMode } from '@/components/BrandMark';
import { PUBLIC_PAGE_BASE_URL } from '@/lib/env';
import type { TermProfile } from '@/lib/terminology';
import { PANEL } from '../copy';
import { LivePreview, type PreviewData } from './LivePreview';

export type PanelSub = keyof typeof PANEL.sub;

export interface PanelProps {
  phase: 'identity' | 'preview';
  /** Identity phase: which value line. */
  sub?: PanelSub;
  mark?: BrandMarkMode;
  /** Preview phase. */
  preview?: PreviewData;
  done?: boolean;
  profile?: TermProfile;
  compact?: boolean;
}

/** The public page's host, as the board prints it («ehjizli.daralulum.com/sara-clinic»). */
export function publicHost(): string {
  try {
    const u = new URL(PUBLIC_PAGE_BASE_URL);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}`;
  } catch {
    return PUBLIC_PAGE_BASE_URL.replace(/^https?:\/\//, '');
  }
}

function Tiles() {
  return (
    <div className="onb-tiles" aria-hidden="true" data-testid="onb-tiles">
      <span className="onb-tile onb-tile--a" style={{ top: 0, insetInlineStart: 0, width: 104, height: 104, borderRadius: 28 }}>
        <Scissors />
      </span>
      <span className="onb-tile onb-tile--photo onb-tile--b" style={{ top: 14, insetInlineStart: 124, width: 160, height: 112 }} data-slot="ob-photo-salon">
        <ImageIcon />
      </span>
      <span className="onb-tile onb-tile--a" style={{ top: 0, insetInlineStart: 318, width: 92, height: 92, borderRadius: 25 }}>
        <Car />
      </span>
      <span className="onb-tile onb-tile--tint onb-tile--b" style={{ top: 136, insetInlineStart: 0, width: 124, height: 124, borderRadius: 33 }}>
        <Stethoscope />
      </span>
      <span className="onb-tile onb-tile--photo onb-tile--a" style={{ top: 146, insetInlineStart: 150, width: 150, height: 150 }} data-slot="ob-photo-clinic">
        <ImageIcon />
      </span>
      <span className="onb-tile onb-tile--b" style={{ top: 120, insetInlineStart: 330, width: 96, height: 96, borderRadius: 26 }}>
        <Utensils />
      </span>
      <span className="onb-tile onb-tile--a" style={{ top: 276, insetInlineStart: 36, width: 84, height: 84, borderRadius: 23 }}>
        <Goal />
      </span>
      <span className="onb-tile onb-tile--photo onb-tile--b" style={{ top: 236, insetInlineStart: 318, width: 138, height: 104 }} data-slot="ob-photo-car">
        <ImageIcon />
      </span>
    </div>
  );
}

export function BrandPanel({ phase, sub = 'role', mark = 'static', preview, done, profile = 'default', compact }: PanelProps) {
  const identity = phase === 'identity' || !preview;
  return (
    <aside className="onb-panel" data-phase={identity ? 'identity' : 'preview'} data-compact={compact ? 'true' : undefined} data-testid="onb-panel">
      <span className="onb-panel-disc onb-panel-disc--a" aria-hidden="true" />
      <span className="onb-panel-disc onb-panel-disc--b" aria-hidden="true" />
      <div className="onb-panel-mark">
        <BrandMark mode={mark} size={compact ? 56 : 64} tone="white" label="احجزلي" data-testid="onb-panel-mark" />
      </div>
      <div className="onb-panel-head">
        {identity ? (
          <>
            <h2 className="onb-panel-title">
              {PANEL.headline[0]}
              <br />
              {PANEL.headline[1]}
            </h2>
            <p className="onb-panel-sub" data-testid="onb-panel-sub">
              {PANEL.sub[sub]}
            </p>
          </>
        ) : (
          <>
            <h2 className="onb-panel-title onb-panel-title--sm">
              {done ? (
                PANEL.doneHeadline
              ) : (
                <>
                  {PANEL.previewHeadline[0]}
                  <br />
                  {PANEL.previewHeadline[1]}
                </>
              )}
            </h2>
            <p className="onb-panel-sub" data-testid="onb-panel-sub">
              {done ? PANEL.doneSub : PANEL.previewSub(profile)}
            </p>
          </>
        )}
      </div>
      <div className="onb-panel-body">
        {identity ? (
          <Tiles />
        ) : (
          <>
            <LivePreview data={preview as PreviewData} done={done} width={compact ? 328 : 380} />
            <div className="onb-panel-link">
              <LinkIcon aria-hidden="true" />
              <bdi dir="ltr" className="ltr" data-testid="onb-slug">
                {publicHost()}/{preview?.slug || '…'}
              </bdi>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
